import { useState } from "react";
import { supabase } from "./supabase";

export default function LandingPage() {
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [working, setWorking] = useState(false);

  async function handleAuth() {
    setWorking(true); setAuthError("");
    if (authMode === "signup") {
      const { error } = await supabase.auth.signUp({ email, password });
      if (error) setAuthError(error.message);
    } else {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) setAuthError(error.message);
    }
    setWorking(false);
  }

  const STEPS = [
    { icon: "💡", title: "Conte sua ideia", desc: "Converse livremente com a IA. Ela faz as perguntas certas para desenvolver seu conceito do zero." },
    { icon: "📖", title: "Construa a Bíblia", desc: "40 campos de worldbuilding preenchidos automaticamente: personagens, mundo, conflito, estrutura e estilo." },
    { icon: "✅", title: "Revisão automática", desc: "A IA lê tudo, detecta inconsistências e corrige automaticamente antes de avançar." },
    { icon: "📋", title: "Monte o roteiro", desc: "Estrutura capítulo a capítulo com 3 atos, pontos de virada, clímax e resolução bem definidos." },
    { icon: "✍️", title: "Escreva com controle", desc: "Cada capítulo é gerado para você aprovar, editar ou pedir reescrita completa — você sempre decide." },
    { icon: "📤", title: "Exporte e publique", desc: "Baixe seu livro em PDF, ePub ou DOCX, prontos para leitura ou publicação." },
  ];

  const FEATURES = [
    { icon: "🌍", title: "Worldbuilding profundo", desc: "Facções, locais, itens significativos, história do mundo e atmosfera sensorial — tudo registrado e usado pela IA." },
    { icon: "🧬", title: "DNA dos personagens", desc: "Ferida emocional, desejo, necessidade, voz e arco. A IA nunca confunde quem é quem." },
    { icon: "📐", title: "Estrutura dos 3 atos", desc: "Incidente incitante, pontos de virada, clímax e resolução mapeados com precisão narrativa." },
    { icon: "📚", title: "Suporte a séries", desc: "Escreva múltiplos livros mantendo continuidade — contexto do Livro 1 passa automaticamente para o Livro 2." },
    { icon: "☁️", title: "Sincronização na nuvem", desc: "Continue de onde parou em qualquer dispositivo — celular, tablet ou computador." },
    { icon: "🎨", title: "Capa com IA", desc: "Gere a capa do seu livro com inteligência artificial. (Em breve)" },
  ];

  const PLANS = [
    {
      name: "Grátis",
      price: "R$0",
      period: "",
      color: "#7a6a50",
      bg: "#f5ede0",
      features: ["1 projeto", "Até 3 capítulos", "PDF básico", "Salvo neste dispositivo"],
      cta: "Começar grátis",
      highlight: false,
    },
    {
      name: "Pro",
      price: "R$59",
      period: "/mês",
      color: "#b8892a",
      bg: "#1a1208",
      features: ["Projetos ilimitados", "Até 30 capítulos por livro", "PDF + ePub + DOCX", "Sincronização na nuvem", "Suporte por e-mail"],
      cta: "Assinar Pro",
      highlight: true,
    },
    {
      name: "Master",
      price: "R$119",
      period: "/mês",
      color: "#d4a94a",
      bg: "#2a1a08",
      features: ["Tudo do Pro", "Séries de múltiplos livros", "Capa com IA (em breve)", "Suporte prioritário", "Acesso antecipado a novidades"],
      cta: "Assinar Master",
      highlight: false,
    },
  ];

  return (
    <div style={{ fontFamily: "'Georgia', serif", background: "#0e0a04", color: "#f0e8d8", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Crimson+Pro:ital,wght@0,400;0,600;1,400&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #0e0a04; }
        .lp-btn { font-family: 'Crimson Pro', Georgia, serif; font-size: 1rem; font-weight: 600; padding: 11px 24px; border-radius: 3px; border: none; cursor: pointer; transition: all .2s; display: inline-flex; align-items: center; gap: 7px; text-decoration: none; }
        .lp-gold { background: linear-gradient(135deg, #b8892a, #d4a94a); color: #1a1208; box-shadow: 0 2px 12px rgba(184,137,42,.35); }
        .lp-gold:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(184,137,42,.5); }
        .lp-outline { background: transparent; border: 1.5px solid rgba(212,169,74,.5); color: #d4a94a; }
        .lp-outline:hover { background: rgba(212,169,74,.08); border-color: #d4a94a; }
        .lp-section { max-width: 1080px; margin: 0 auto; padding: 0 24px; }
        .lp-nav { position: sticky; top: 0; z-index: 100; background: rgba(14,10,4,.92); backdrop-filter: blur(12px); border-bottom: 1px solid rgba(184,137,42,.15); }
        .lp-nav-inner { max-width: 1080px; margin: 0 auto; padding: 16px 24px; display: flex; align-items: center; justify-content: space-between; }
        .lp-logo { font-family: 'Playfair Display', serif; font-size: 1.5rem; font-weight: 900; color: #f0e8d8; letter-spacing: -0.5px; }
        .lp-logo span { color: #d4a94a; }
        .lp-hero { text-align: center; padding: 100px 24px 80px; position: relative; overflow: hidden; }
        .lp-hero::before { content: ''; position: absolute; top: -200px; left: 50%; transform: translateX(-50%); width: 700px; height: 700px; background: radial-gradient(ellipse, rgba(184,137,42,.08) 0%, transparent 70%); pointer-events: none; }
        .lp-badge { display: inline-flex; align-items: center; gap: 6px; background: rgba(184,137,42,.12); border: 1px solid rgba(184,137,42,.3); border-radius: 99px; padding: 5px 14px; font-size: .8rem; color: #d4a94a; letter-spacing: .5px; text-transform: uppercase; margin-bottom: 28px; }
        .lp-h1 { font-family: 'Playfair Display', serif; font-size: clamp(2.4rem, 6vw, 4rem); font-weight: 900; line-height: 1.15; color: #f0e8d8; margin-bottom: 22px; }
        .lp-h1 em { color: #d4a94a; font-style: normal; }
        .lp-sub { font-family: 'Crimson Pro', Georgia, serif; font-size: clamp(1.05rem, 2.2vw, 1.3rem); color: rgba(240,232,216,.65); max-width: 600px; margin: 0 auto 40px; line-height: 1.7; }
        .lp-ctas { display: flex; gap: 14px; justify-content: center; flex-wrap: wrap; margin-bottom: 60px; }
        .lp-stats { display: flex; gap: 40px; justify-content: center; flex-wrap: wrap; border-top: 1px solid rgba(184,137,42,.15); padding-top: 40px; }
        .lp-stat { text-align: center; }
        .lp-stat-num { font-family: 'Playfair Display', serif; font-size: 2rem; font-weight: 700; color: #d4a94a; }
        .lp-stat-label { font-size: .8rem; color: rgba(240,232,216,.5); letter-spacing: .5px; text-transform: uppercase; margin-top: 2px; }
        .lp-steps-section { padding: 90px 0; background: #120d06; border-top: 1px solid rgba(184,137,42,.1); border-bottom: 1px solid rgba(184,137,42,.1); }
        .lp-section-label { font-size: .75rem; color: #b8892a; letter-spacing: 2px; text-transform: uppercase; text-align: center; margin-bottom: 12px; }
        .lp-section-title { font-family: 'Playfair Display', serif; font-size: clamp(1.6rem, 3.5vw, 2.4rem); font-weight: 700; text-align: center; margin-bottom: 56px; color: #f0e8d8; }
        .lp-steps { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px; }
        .lp-step { display: flex; gap: 18px; align-items: flex-start; padding: 22px; background: rgba(255,255,255,.03); border: 1px solid rgba(184,137,42,.12); border-radius: 6px; transition: border-color .2s; }
        .lp-step:hover { border-color: rgba(184,137,42,.3); }
        .lp-step-num { flex-shrink: 0; width: 36px; height: 36px; border-radius: 50%; background: rgba(184,137,42,.15); border: 1px solid rgba(184,137,42,.3); display: flex; align-items: center; justify-content: center; font-size: 1.1rem; }
        .lp-step-title { font-family: 'Playfair Display', serif; font-size: 1.05rem; color: #f0e8d8; margin-bottom: 6px; }
        .lp-step-desc { font-size: .9rem; color: rgba(240,232,216,.55); line-height: 1.6; }
        .lp-features-section { padding: 90px 0; }
        .lp-features { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 20px; }
        .lp-feature { padding: 24px; background: rgba(255,255,255,.025); border: 1px solid rgba(184,137,42,.1); border-radius: 6px; transition: all .2s; }
        .lp-feature:hover { background: rgba(184,137,42,.04); border-color: rgba(184,137,42,.25); transform: translateY(-2px); }
        .lp-feature-icon { font-size: 1.8rem; margin-bottom: 12px; }
        .lp-feature-title { font-family: 'Playfair Display', serif; font-size: 1.05rem; color: #f0e8d8; margin-bottom: 8px; }
        .lp-feature-desc { font-size: .88rem; color: rgba(240,232,216,.5); line-height: 1.65; }
        .lp-plans-section { padding: 90px 0; background: #120d06; border-top: 1px solid rgba(184,137,42,.1); border-bottom: 1px solid rgba(184,137,42,.1); }
        .lp-plans { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 20px; max-width: 860px; margin: 0 auto; }
        .lp-plan { padding: 28px; border-radius: 6px; border: 1px solid rgba(184,137,42,.15); background: rgba(255,255,255,.025); position: relative; }
        .lp-plan.highlight { border-color: #b8892a; background: rgba(184,137,42,.08); box-shadow: 0 0 0 1px rgba(184,137,42,.3), 0 20px 40px rgba(0,0,0,.3); }
        .lp-plan-badge { position: absolute; top: -12px; left: 50%; transform: translateX(-50%); background: linear-gradient(135deg,#b8892a,#d4a94a); color: #1a1208; padding: 3px 16px; border-radius: 99px; font-size: .72rem; font-weight: 700; letter-spacing: .5px; text-transform: uppercase; white-space: nowrap; }
        .lp-plan-name { font-family: 'Playfair Display', serif; font-size: 1.1rem; color: rgba(240,232,216,.7); margin-bottom: 8px; }
        .lp-plan-price { font-family: 'Playfair Display', serif; font-size: 2.4rem; font-weight: 700; color: #f0e8d8; margin-bottom: 4px; }
        .lp-plan-period { font-size: .85rem; color: rgba(240,232,216,.4); }
        .lp-plan-divider { border: none; border-top: 1px solid rgba(184,137,42,.15); margin: 20px 0; }
        .lp-plan-features { list-style: none; display: flex; flex-direction: column; gap: 10px; margin-bottom: 24px; }
        .lp-plan-features li { font-size: .9rem; color: rgba(240,232,216,.65); display: flex; align-items: center; gap: 8px; }
        .lp-plan-features li::before { content: '✓'; color: #b8892a; font-weight: 700; flex-shrink: 0; }
        .lp-cta-section { padding: 100px 0; text-align: center; }
        .lp-footer { border-top: 1px solid rgba(184,137,42,.12); padding: 36px 24px; text-align: center; }
        .lp-footer-logo { font-family: 'Playfair Display', serif; font-size: 1.2rem; font-weight: 700; margin-bottom: 8px; }
        .lp-footer-logo span { color: #d4a94a; }
        .lp-footer-tagline { font-size: .82rem; color: rgba(240,232,216,.35); }
        /* Auth modal */
        .lp-modal-overlay { position: fixed; inset: 0; background: rgba(14,10,4,.85); z-index: 500; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .lp-modal { background: #1a1208; border: 1px solid rgba(184,137,42,.3); border-radius: 8px; padding: 36px; max-width: 400px; width: 100%; box-shadow: 0 24px 60px rgba(0,0,0,.6); }
        .lp-modal input { font-family: 'Crimson Pro', Georgia, serif; font-size: 1rem; background: rgba(255,255,255,.05); border: 1px solid rgba(184,137,42,.25); border-radius: 3px; padding: 10px 14px; color: #f0e8d8; outline: none; width: 100%; transition: border-color .2s; }
        .lp-modal input:focus { border-color: #b8892a; box-shadow: 0 0 0 3px rgba(184,137,42,.1); }
        .lp-modal input::placeholder { color: rgba(240,232,216,.3); }
        .lp-modal label { font-size: .72rem; font-weight: 600; color: rgba(240,232,216,.45); text-transform: uppercase; letter-spacing: .8px; display: block; margin-bottom: 5px; }
        .lp-spin { width: 15px; height: 15px; border: 2px solid rgba(184,137,42,.3); border-top-color: #b8892a; border-radius: 50%; animation: lpspin .7s linear infinite; display: inline-block; }
        @keyframes lpspin { to { transform: rotate(360deg) } }
        @media (max-width: 600px) {
          .lp-hero { padding: 70px 20px 60px; }
          .lp-stats { gap: 24px; }
          .lp-steps-section, .lp-features-section, .lp-plans-section, .lp-cta-section { padding: 60px 0; }
          .lp-nav-inner { padding: 14px 16px; }
          .lp-section { padding: 0 16px; }
        }
      `}</style>

      {/* NAV */}
      <nav className="lp-nav">
        <div className="lp-nav-inner">
          <div className="lp-logo">Autor<span>IA</span></div>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="lp-btn lp-outline" onClick={() => { setAuthMode("login"); setShowAuth(true); setAuthError(""); }}>
              Entrar
            </button>
            <button className="lp-btn lp-gold" onClick={() => { setAuthMode("signup"); setShowAuth(true); setAuthError(""); }}>
              Começar grátis
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-badge">✦ Inteligência Artificial Generativa</div>
        <h1 className="lp-h1">
          Escreva seu livro.<br />
          <em>A IA cuida da estrutura.</em>
        </h1>
        <p className="lp-sub">
          Do brainstorming ao manuscrito final — com uma IA que conhece cada detalhe do seu mundo literário, dos personagens ao clímax.
        </p>
        <div className="lp-ctas">
          <button className="lp-btn lp-gold" style={{ fontSize: "1.05rem", padding: "13px 30px" }}
            onClick={() => { setAuthMode("signup"); setShowAuth(true); setAuthError(""); }}>
            Começar grátis →
          </button>
          <button className="lp-btn lp-outline"
            onClick={() => { setAuthMode("login"); setShowAuth(true); setAuthError(""); }}>
            Já tenho conta
          </button>
        </div>
        <div className="lp-stats">
          <div className="lp-stat">
            <div className="lp-stat-num">40</div>
            <div className="lp-stat-label">Campos de worldbuilding</div>
          </div>
          <div className="lp-stat">
            <div className="lp-stat-num">3</div>
            <div className="lp-stat-label">Formatos de exportação</div>
          </div>
          <div className="lp-stat">
            <div className="lp-stat-num">100%</div>
            <div className="lp-stat-label">Controle do autor</div>
          </div>
          <div className="lp-stat">
            <div className="lp-stat-num">☁️</div>
            <div className="lp-stat-label">Sincronizado na nuvem</div>
          </div>
        </div>
      </section>

      {/* COMO FUNCIONA */}
      <section className="lp-steps-section">
        <div className="lp-section">
          <div className="lp-section-label">Como funciona</div>
          <h2 className="lp-section-title">Do zero ao livro publicável em 6 etapas</h2>
          <div className="lp-steps">
            {STEPS.map((s, i) => (
              <div key={i} className="lp-step">
                <div className="lp-step-num">{s.icon}</div>
                <div>
                  <div className="lp-step-title">{i + 1}. {s.title}</div>
                  <div className="lp-step-desc">{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FUNCIONALIDADES */}
      <section className="lp-features-section">
        <div className="lp-section">
          <div className="lp-section-label">Funcionalidades</div>
          <h2 className="lp-section-title">Tudo que um autor precisa</h2>
          <div className="lp-features">
            {FEATURES.map((f, i) => (
              <div key={i} className="lp-feature">
                <div className="lp-feature-icon">{f.icon}</div>
                <div className="lp-feature-title">{f.title}</div>
                <div className="lp-feature-desc">{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLANOS */}
      <section className="lp-plans-section">
        <div className="lp-section">
          <div className="lp-section-label">Planos</div>
          <h2 className="lp-section-title">Escolha o seu plano</h2>
          <div className="lp-plans">
            {PLANS.map((p, i) => (
              <div key={i} className={`lp-plan${p.highlight ? " highlight" : ""}`}>
                {p.highlight && <div className="lp-plan-badge">⭐ Mais popular</div>}
                <div className="lp-plan-name">{p.name}</div>
                <div>
                  <span className="lp-plan-price">{p.price}</span>
                  <span className="lp-plan-period">{p.period}</span>
                </div>
                <hr className="lp-plan-divider" />
                <ul className="lp-plan-features">
                  {p.features.map((f, j) => <li key={j}>{f}</li>)}
                </ul>
                <button
                  className="lp-btn lp-gold"
                  style={{ width: "100%", justifyContent: "center", opacity: p.highlight ? 1 : 0.8 }}
                  onClick={() => { setAuthMode("signup"); setShowAuth(true); setAuthError(""); }}
                >
                  {p.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="lp-cta-section">
        <div className="lp-section">
          <div style={{ fontSize: "2rem", marginBottom: 20 }}>✦</div>
          <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 4vw, 2.8rem)", fontWeight: 700, color: "#f0e8d8", marginBottom: 16, lineHeight: 1.2 }}>
            Seu próximo livro começa agora.
          </h2>
          <p style={{ fontFamily: "'Crimson Pro', Georgia, serif", fontSize: "1.15rem", color: "rgba(240,232,216,.55)", marginBottom: 36, maxWidth: 480, margin: "0 auto 36px" }}>
            Junte-se a autores que já usam IA para escrever mais rápido, com mais consistência e sem perder a própria voz.
          </p>
          <button
            className="lp-btn lp-gold"
            style={{ fontSize: "1.1rem", padding: "14px 36px" }}
            onClick={() => { setAuthMode("signup"); setShowAuth(true); setAuthError(""); }}
          >
            Criar minha conta grátis →
          </button>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lp-footer">
        <div className="lp-footer-logo">Autor<span>IA</span></div>
        <div className="lp-footer-tagline">A IA que escreve com você, não por você.</div>
      </footer>

      {/* AUTH MODAL */}
      {showAuth && (
        <div className="lp-modal-overlay" onClick={() => setShowAuth(false)}>
          <div className="lp-modal" onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.7rem", fontWeight: 900, color: "#f0e8d8", marginBottom: 4 }}>
                Autor<span style={{ color: "#d4a94a" }}>IA</span>
              </div>
              <div style={{ fontSize: ".85rem", color: "rgba(240,232,216,.45)" }}>
                {authMode === "login" ? "Bem-vindo de volta" : "Crie sua conta gratuitamente"}
              </div>
            </div>

            {authError && (
              <div style={{ background: "rgba(139,32,32,.2)", border: "1px solid rgba(240,100,100,.3)", color: "#f08080", borderRadius: 4, padding: "10px 14px", fontSize: ".85rem", marginBottom: 14 }}>
                {authError}
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label>E-mail</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  onKeyDown={e => e.key === "Enter" && handleAuth()}
                  autoFocus
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                <label>Senha</label>
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="mínimo 6 caracteres"
                  onKeyDown={e => e.key === "Enter" && handleAuth()}
                />
              </div>
            </div>

            <button
              className="lp-btn lp-gold"
              style={{ width: "100%", justifyContent: "center", marginBottom: 14, fontSize: "1rem", padding: "12px" }}
              onClick={handleAuth}
              disabled={working || !email || !password}
            >
              {working
                ? <><span className="lp-spin" /> Aguarde...</>
                : authMode === "login" ? "Entrar" : "Criar conta"}
            </button>

            <div style={{ textAlign: "center", fontSize: ".82rem", color: "rgba(240,232,216,.4)" }}>
              {authMode === "login" ? (
                <>Não tem conta?{" "}
                  <button onClick={() => { setAuthMode("signup"); setAuthError(""); }}
                    style={{ background: "none", border: "none", color: "#d4a94a", cursor: "pointer", fontWeight: 700, fontSize: ".82rem" }}>
                    Cadastre-se
                  </button>
                </>
              ) : (
                <>Já tem conta?{" "}
                  <button onClick={() => { setAuthMode("login"); setAuthError(""); }}
                    style={{ background: "none", border: "none", color: "#d4a94a", cursor: "pointer", fontWeight: 700, fontSize: ".82rem" }}>
                    Entrar
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
